import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string;
  height?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ 
  className = "", 
  width = "100%", 
  height = "1rem" 
}) => {
  return (
    <div 
      className={`animate-pulse bg-gray-200 rounded ${className}`}
      style={{ width, height }}
    />
  );
};

// Restaurant card skeleton
export const RestaurantCardSkeleton: React.FC = () => {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <Skeleton height="12rem" className="w-full" />
      <div className="p-4 space-y-3">
        <Skeleton height="1.5rem" width="75%" />
        <Skeleton height="1rem" width="50%" />
        <div className="flex justify-between items-center">
          <Skeleton height="1rem" width="30%" />
          <Skeleton height="1rem" width="20%" />
        </div>
        <div className="flex items-center space-x-2">
          <Skeleton height="1rem" width="15%" className="rounded-full" />
          <Skeleton height="1rem" width="25%" />
        </div>
      </div>
    </div>
  );
};

// Menu item skeleton
export const MenuItemSkeleton: React.FC = () => {
  return (
    <div className="flex space-x-4 p-4 border-b border-gray-100">
      <Skeleton height="4rem" width="4rem" className="rounded-lg flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton height="1.25rem" width="70%" />
        <Skeleton height="0.875rem" width="90%" />
        <Skeleton height="0.875rem" width="60%" />
        <div className="flex justify-between items-center mt-2">
          <Skeleton height="1rem" width="20%" />
          <Skeleton height="2rem" width="6rem" className="rounded-full" />
        </div>
      </div>
    </div>
  );
};

// Restaurant detail skeleton
export const RestaurantDetailSkeleton: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header skeleton */}
      <div className="mb-6">
        <Skeleton height="2rem" width="60%" className="mb-2" />
        <div className="flex items-center space-x-4 mb-4">
          <Skeleton height="1rem" width="30%" />
          <Skeleton height="1rem" width="25%" />
          <Skeleton height="1rem" width="20%" />
        </div>
        <Skeleton height="1rem" width="80%" />
      </div>

      {/* Menu sections skeleton */}
      <div className="space-y-6">
        {[1, 2, 3].map((section) => (
          <div key={section}>
            <Skeleton height="1.5rem" width="40%" className="mb-4" />
            <div className="space-y-4">
              {[1, 2, 3, 4].map((item) => (
                <MenuItemSkeleton key={item} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Restaurant list skeleton
export const RestaurantListSkeleton: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Search bar skeleton */}
      <div className="mb-6">
        <Skeleton height="3rem" className="mb-4" />
        <div className="flex space-x-4">
          <Skeleton height="2.5rem" width="8rem" />
          <Skeleton height="2.5rem" width="8rem" />
          <Skeleton height="2.5rem" width="8rem" />
        </div>
      </div>

      {/* Restaurant grid skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <RestaurantCardSkeleton key={item} />
        ))}
      </div>
    </div>
  );
};

// Cart skeleton
export const CartSkeleton: React.FC = () => {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((item) => (
        <div key={item} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
          <Skeleton height="3rem" width="3rem" className="rounded" />
          <div className="flex-1 space-y-2">
            <Skeleton height="1rem" width="70%" />
            <Skeleton height="0.875rem" width="50%" />
          </div>
          <div className="text-right space-y-2">
            <Skeleton height="1rem" width="3rem" />
            <Skeleton height="2rem" width="6rem" />
          </div>
        </div>
      ))}
      <div className="border-t pt-4">
        <div className="flex justify-between items-center mb-4">
          <Skeleton height="1.25rem" width="4rem" />
          <Skeleton height="1.25rem" width="5rem" />
        </div>
        <Skeleton height="3rem" className="w-full" />
      </div>
    </div>
  );
};